import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import styled from 'styled-components';
import IP_URL from '../connect';
import Navbar from '../Navbar';
import Footer from '../Footer';
import Sidebar from '../Sidebar';
import meterIconImg from '../../Assets/meter.png';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import { useSelector } from 'react-redux';
import { selectUser } from '../../Redux/currentUserSlice';
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

// ---------- Fix Leaflet Icon ----------
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: meterIconImg,
  iconUrl: meterIconImg,
  shadowUrl: null,
});

// ---------- Styled Components ----------
const Main = styled.div`
  background-color: #f4f6f8;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const View = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 70px;
  right: 20px;
  z-index: 1000;
  padding: 10px 18px;
  border: none;
  background-color: ${({ active }) => (active ? '#dc3545' : '#007bff')};
  color: white;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &:hover {
    background-color: ${({ active }) => (active ? '#c82333' : '#0056b3')};
  }

  @media (max-width: 768px) {
    top: 75px;
    right: 10px;
    font-size: 12px;
    padding: 8px 14px;
  }
`;

const LoadingText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.2rem;
  color: #007bff;
  font-weight: 500;
`;

// ---------- Component ----------
const OrganizationMeterLocations = () => {
  const navigate = useNavigate();
  const [showNames, setShowNames] = useState(false);
  const [meters, setMeters] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userLocation, setUserLocation] = useState([-1.344374, 36.797811]);
  const [loading, setLoading] = useState(false);
  const { token, uniqueKey } = useSelector(selectUser);

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  // ---------- Fetch Meters ----------
  useEffect(() => {
    const fetchMeters = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${IP_URL}/meter/org-meter-locations?uniqueKey=${uniqueKey}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch meters");

        const data = await response.json();
        setMeters(data);
        toast.success(`Fetched ${data.length} meter locations successfully!`, { autoClose: 2000 });
      } catch (error) {
        console.error("Error fetching meters:", error);
        toast.error("Unable to load meter locations. Please try again.", { autoClose: 4000 });
      } finally {
        setLoading(false);
      }
    };

    fetchMeters();
  }, [token, uniqueKey]);

  const navigateToMeterDetails = (imei) => navigate(`/meter-details/${imei}`);

  return (
    <Main>
      <Navbar showMenuButton={true} toggleSidebar={toggleSidebar} />
      <View>
        <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

        <ToggleButton active={showNames} onClick={() => setShowNames(!showNames)}>
          {showNames ? "Hide Meter Names" : "Show Meter Names"}
        </ToggleButton>

        {loading ? (
          <LoadingText>Loading meter data...</LoadingText>
        ) : (
          <MapContainer
            center={userLocation}
            zoom={13}
            style={{
              width: "100%",
              height: "calc(100vh - 150px)",
              borderRadius: "10px",
            }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>'
            />

            {meters.map((meter) => (
              <Marker
                key={meter._id || meter.id}
                position={[meter.lat, meter.lng]}
                icon={new L.Icon({
                  iconUrl: meterIconImg,
                  iconSize: [25, 35],
                  iconAnchor: [12, 35],
                })}
                eventHandlers={{
                  click: () => navigateToMeterDetails(meter.id),
                }}
              >
                {showNames && (
                  <Tooltip
                    permanent
                    direction="top"
                    offset={[0, -20]}
                    opacity={1}
                    className="meter-tooltip"
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#0056b3',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigateToMeterDetails(meter.id)}
                    >
                      {meter.meterName || "Unnamed Meter"}
                    </span>
                  </Tooltip>
                )}
              </Marker>
            ))}
          </MapContainer>
        )}
      </View>
      <Footer />

    </Main>
  );
};

export default OrganizationMeterLocations;
